import {
  createMacroTimer,
  startFocus,
  pauseFocus,
  resumeFocus,
  requestFinish,
  confirmFinish,
  discardSession,
  addDistraction,
  addReadingTime,
  elapsedFocusMs,
  tickFocus,
  tickBreak
} from './macro-timer';
import {
  createProblemTimer,
  startProblem as startProblemTimer,
  stopProblem as stopProblemTimer,
  resetProblem as resetProblemTimer,
  tickCountdown
} from './problem-timer';
import type {
  MacroTimer,
  ProblemTimer,
  SessionMode,
  MacroTimerState,
  SessionContext
} from './types';
import type { AttemptResult, Conjecture, ProblemAttempt, RecallItem, ReviewRating, UUID } from '$domain/types';
import {
  createMacroSession,
  finalizeMacroSession,
  getOpenMacroSession,
  updateMacroSession,
  createProblemAttempt,
  listAttemptsBySession,
  getMaxProblemNumberForNode,
  createRecallItem,
  listActiveRecallItems,
  listDueRecallItems,
  listRecallItemsByAttempt,
  softDeleteRecallItem,
  updateRecallAfterReview,
  createReviewEvent,
  createConjecture,
  deleteConjecture,
  listConjecturesBySession,
  softDeleteProblemAttempt,
  resolveFsrsForNode,
  type ResolvedFsrs,
  newId
} from '$db/local/repositories';
import type { MacroTimerSnapshot } from '$domain/types';
import { toast } from '$ui/primitives/Toast';
import {
  newFsrsState,
  scheduleReview,
  scheduleToState,
  RATING_AGAIN
} from '$core/fsrs';

const DEFAULT_FSRS_RETENTION = (() => {
  const raw = import.meta.env.VITE_FSRS_RETENTION_TARGET;
  const n = raw ? Number.parseFloat(raw) : NaN;
  return Number.isFinite(n) && n > 0 && n < 1 ? n : 0.9;
})();

const DEFAULT_MAX_INTERVAL_DAYS = 365;
import { buildRecallGroups, type RecallGroup } from './recall-groups';
import {
  computeForecast,
  computeStats,
  dayKeyFromIso,
  type ForecastDay,
  type ForecastStats
} from '$features/forecast';

interface AttemptSnapshot {
  macroSessionId: UUID;
  context: SessionContext;
  problemNumber: number;
  durationMs: number;
  result: AttemptResult;
  notes: string;
  errorTaxonomy: string | null;
}

export class SessionStore {
  macro: MacroTimer = $state(createMacroTimer());
  problem: ProblemTimer = $state(createProblemTimer());
  mode: SessionMode = $state('A');
  problemNumber: number = $state(1);
  lastMessage: string | null = $state(null);
  previousMacroState: MacroTimerState = 'focus_running';

  currentResult: AttemptResult | null = $state(null);
  currentNotes: string = $state('');
  currentErrorTaxonomy: string | null = $state(null);

  context: SessionContext | null = $state(null);
  /**
   * Contexto congelado al arrancar el timer del problema. Asegura que
   * cada attempt pertenece a UNA cadena única — aunque el usuario cambie
   * cualquier select mientras el timer corre, el attempt en curso se
   * persiste con esta cadena. Se limpia al hacer next / discard.
   */
  attemptContext: SessionContext | null = $state(null);
  macroSessionId: UUID | null = $state(null);
  
  // Seguimiento de tiempo por nodo durante la sesión
  nodeTimes: Record<string, number> = $state({});
  activeNodeId: string | null = $state(null);
  contextChangedAt: number | null = $state(null);

  // Inyectado desde App.svelte. Null en tests aislados.
  private prefs: { prefs: { problemTimerMode: 'timer' | 'countdown'; countdownDurationMs: number; alarmEnabled: boolean } } | null = null;

  setPrefsStore(p: { prefs: { problemTimerMode: 'timer' | 'countdown'; countdownDurationMs: number; alarmEnabled: boolean } }): void {
    this.prefs = p;
  }

  reviewGroups: RecallGroup[] = $state([]);
  reviewLoading: boolean = $state(false);
  selectedGroupKey: string | null = $state(null);
  reviewBatch: RecallItem[] = $state([]);
  reviewIndex: number = $state(0);
  reviewConsultedSolution: boolean = $state(false);
  reviewElapsedDays: number = $state(0);
  currentRecallOutcome: 'good' | 'again' | null = $state(null);
  currentRecallRated: boolean = $state(false);
  dbMutationVersion: number = $state(0);

  attemptsCorrect: number = $state(0);
  attemptsIncorrect: number = $state(0);
  attemptsUnresolved: number = $state(0);
  attemptsTotalMs: number = $state(0);

  forecastDays: ForecastDay[] = $state([]);
  forecastStats: ForecastStats = $state({
    dueToday: 0,
    active: 0,
    avgStability: 0,
    avgDifficulty: 0
  });
  selectedForecastDate: string | null = $state(null);
  forecastItemsForDate: RecallItem[] = $state([]);

  private pendingPersists: Promise<void>[] = [];

  // --- DB-as-source-of-truth helpers (M4.5b) ---

  private toSnapshot(timer: MacroTimer): MacroTimerSnapshot {
    return {
      state: timer.state,
      focusDurationMs: timer.focusDurationMs,
      breakDurationMs: timer.breakDurationMs,
      openEnded: timer.openEnded,
      readingAccumulatedMs: timer.readingAccumulatedMs,
      focusStartedAt: timer.focusStartedAt,
      breakStartedAt: timer.breakStartedAt,
      focusAccumulatedMs: timer.focusAccumulatedMs,
      breakAccumulatedMs: timer.breakAccumulatedMs,
      distractions: timer.distractions,
      pausedAt: timer.pausedAt
    };
  }

  private fromSnapshot(snap: MacroTimerSnapshot): MacroTimer {
    return {
      state: snap.state,
      focusDurationMs: snap.focusDurationMs,
      breakDurationMs: snap.breakDurationMs,
      openEnded: snap.openEnded,
      readingDurationMs: snap.readingAccumulatedMs,
      focusStartedAt: snap.focusStartedAt,
      breakStartedAt: snap.breakStartedAt,
      focusAccumulatedMs: snap.focusAccumulatedMs,
      breakAccumulatedMs: snap.breakAccumulatedMs,
      readingAccumulatedMs: snap.readingAccumulatedMs,
      distractions: snap.distractions,
      pausedAt: snap.pausedAt
    };
  }

  private persistSnapshot(): void {
    if (!this.macroSessionId) return;
    const id = this.macroSessionId;
    const snap = this.toSnapshot(this.macro);
    this.trackPersist(
      updateMacroSession(id, {
        status: this.macro.state,
        timerSnapshot: snap,
        readingDurationMs: this.macro.readingAccumulatedMs,
        distractions: this.macro.distractions
      })
    );
  }

  /**
   * Reconstruye el estado del timer desde la DB.
   * Llamar al arrancar la app y al recibir eventos cross-tab.
   * Devuelve true si había una sesión abierta (y se hidrató).
   */
  async reconstructFromDb(): Promise<boolean> {
    const open = await getOpenMacroSession();
    if (!open) return false;
    if (this.macroSessionId === open.id) return true;

    this.macroSessionId = open.id;
    this.context = {
      nodeId: open.nodeId,
      path: [open.nodeId]
    };
    this.activeNodeId = open.nodeId;
    this.nodeTimes = open.nodeTimes ?? {};
    this.contextChangedAt = Date.now();
    this.mode = open.mode;

    if (open.timerSnapshot) {
      this.macro = this.fromSnapshot(open.timerSnapshot);
    } else {
      // Sesión antigua sin snapshot: reconstruir mínimo.
      this.macro = {
        ...createMacroTimer(open.focusDurationMs, 0, false),
        state: open.status,
        focusAccumulatedMs: open.focusDurationMs,
        readingAccumulatedMs: open.readingDurationMs,
        distractions: open.distractions,
        focusStartedAt: open.status === 'focus_running' ? Date.now() : null,
        pausedAt: open.status === 'focus_paused' ? Date.now() : null
      };
    }
    return true;
  }

  // --- Problem number auto-recalc ---

  /**
   * Recalcula el Problem # para la cadena actual (match EXACTO de los 5
   * campos subject > subtopic > book > chapter > section). Se llama al
   * arrancar sesión y al cambiar cualquier select mientras el timer del
   * problema está idle.
   *
   * Si cambias Section (ej. de "Resueltos" a "Propuestos"), el contador
   * vuelve a 1 porque esa sección del libro no tiene intentos previos.
   */
  private async recalcProblemNumber(): Promise<void> {
    if (!this.context) return;
    if (this.problem.state !== 'idle') return;
    try {
      const max = await getMaxProblemNumberForNode(this.context.nodeId!);
      this.problemNumber = max + 1;
    } catch (err) {
      console.error('[StudyQuantifier] recalcProblemNumber failed:', err);
    }
  }

  // --- Problem timer actions ---

  updateProblemTimerSettings(mode: 'timer' | 'countdown', durationMs: number): void {
    if (this.problem.state === 'running') return;
    if (!this.prefs) {
      console.warn('[SessionStore] prefs not injected; call setPrefsStore first');
      return;
    }
    const validMode = mode === 'countdown' ? 'countdown' : 'timer';
    const clamped = Math.max(1_000, Math.min(3_600_000, Math.floor(durationMs)));
    this.prefs.prefs.problemTimerMode = validMode;
    this.prefs.prefs.countdownDurationMs = clamped;
    // CRÍTICO: sincronizar el ProblemTimer actual para que la UI refleje
    // el cambio de inmediato. Sin esto, el botón se ve inactivo aunque
    // el click haya funcionado (bug de "no puedo cambiar de modo").
    this.problem = {
      ...this.problem,
      mode: validMode,
      countdownDurationMs: clamped
    };
  }

  startProblem(): void {
    if (this.problem.state !== 'idle') return;

    const timerOpts = {
      mode: this.prefs?.prefs.problemTimerMode ?? 'timer',
      countdownDurationMs: this.prefs?.prefs.countdownDurationMs ?? 0
    };

    if (this.mode === 'B') {
      if (!this.currentRecall) {
        toast.error('Elige un grupo para empezar.');
        return;
      }
      this.attemptContext = this.context ? { ...this.context } : null;
      this.problem = startProblemTimer(this.problem, timerOpts);
      return;
    }

    if (this.macro.state === 'focus_paused') {
      toast.error('El bloque está en pausa. Reanúdalo para cronometrar.');
      return;
    }
    if (this.macro.state === 'break_running' || this.macro.state === 'break_paused') {
      toast.info('Estás en descanso. Vuelve al foco para cronometrar.');
      return;
    }
    if (this.macro.state !== 'focus_running') {
      toast.error('No hay un bloque activo. Inicia uno primero.');
      return;
    }
    this.attemptContext = this.context ? { ...this.context } : null;
    this.problem = startProblemTimer(this.problem, timerOpts);
  }

  stopProblem(): void {
    if (this.problem.state !== 'running') return;
    this.problem = stopProblemTimer(this.problem);
  }

  toggleProblem(): void {
    if (this.problem.state === 'idle') {
      this.startProblem();
    } else if (this.problem.state === 'running') {
      this.stopProblem();
    }
  }

  rateProblem(result: AttemptResult): void {
    if (this.problem.state !== 'evaluating') return;
    this.currentResult = result;
    if (result === 'correct') {
      this.currentErrorTaxonomy = null;
    }
    this.lastMessage = null;
  }

  setNotes(notes: string): void {
    this.currentNotes = notes;
  }

  setErrorTaxonomy(code: string | null): void {
    if (this.currentResult === 'correct') return;
    this.currentErrorTaxonomy = code;
  }

  private clearCurrentAttempt(): void {
    this.currentResult = null;
    this.currentNotes = '';
    this.currentErrorTaxonomy = null;
  }

  nextProblem(): void {
    if (this.problem.state !== 'evaluating') return;
    if (this.macro.state !== 'focus_running' && this.macro.state !== 'break_running') return;
    if (this.currentResult === null) {
      toast.error('Califica el intento para continuar.');
      return;
    }
    const snapshot = this.snapshotCurrentAttempt();
    const changedContext =
      !!this.attemptContext &&
      !!this.context &&
      this.attemptContext.nodeId !== this.context.nodeId;

    this.clearCurrentAttempt();
    this.attemptContext = null;
    this.problem = resetProblemTimer(this.problem);

    if (changedContext) {
      // El contexto cambió mid-problem: recalcular el contador para la
      // nueva cadena en lugar de incrementar sobre el anterior.
      void this.recalcProblemNumber();
    } else {
      this.problemNumber += 1;
    }

    this.lastMessage = null;
    if (snapshot) this.trackPersist(this.persistAttempt(snapshot));
  }

  private snapshotCurrentAttempt(): AttemptSnapshot | null {
    // El attempt pertenece al contexto congelado al arrancar el timer,
    // NO al contexto que el usuario pueda haber cambiado mientras corría.
    const ctx = this.attemptContext ?? this.context;
    if (!ctx) return null;
    if (!this.macroSessionId) return null;
    if (this.currentResult === null) return null;
    return {
      macroSessionId: this.macroSessionId,
      context: ctx,
      problemNumber: this.problemNumber,
      durationMs: this.problem.elapsedMs,
      result: this.currentResult,
      notes: this.currentNotes,
      errorTaxonomy: this.currentErrorTaxonomy
    };
  }

  private bumpDbMutation(): void {
    this.dbMutationVersion += 1;
  }

  private trackPersist(p: Promise<void>): void {
    this.pendingPersists.push(p);
    p.finally(() => {
      this.pendingPersists = this.pendingPersists.filter((x) => x !== p);
    });
  }

  private async persistAttempt(snap: AttemptSnapshot): Promise<void> {
    if (snap.result === 'correct') this.attemptsCorrect += 1;
    else if (snap.result === 'incorrect') this.attemptsIncorrect += 1;
    else this.attemptsUnresolved += 1;
    this.attemptsTotalMs += snap.durationMs;

    try {
      const attempt = await createProblemAttempt({
        macroSessionId: snap.macroSessionId,
        nodeId: snap.context.nodeId!,
        problemNumber: String(snap.problemNumber),
        durationMs: snap.durationMs,
        result: snap.result,
        errorTaxonomy: snap.errorTaxonomy,
        notes: snap.notes || null
      });

      // Bump SIEMPRE, pero diferido a un microtask para dar tiempo a
      // Dexie a terminar el commit real a IndexedDB. Sin esto, los reads
      // de paneles como RecentProblems disparados inmediatamente después
      // del await pueden no ver el row recién añadido por aislamiento
      // transaccional de IndexedDB. Ese era el bug del "no se muestran
      // los problemas nuevos en la tabla".
      queueMicrotask(() => this.bumpDbMutation());

      if (snap.result === 'incorrect' || snap.result === 'unresolved') {
        const initial = newFsrsState();
        const fsrs = await this.resolveFsrs(snap.context.nodeId!);
        const retentionTarget = fsrs.retentionTarget;
        const maximumInterval = fsrs.maximumIntervalDays;
        const sched = scheduleReview(initial, RATING_AGAIN, {
          params: { retentionTarget, maximumInterval }
        });
        const _state = scheduleToState(sched);
        void _state;
        await createRecallItem({
          nodeId: snap.context.nodeId!,
          originalAttemptId: attempt.id,
          originalProblemNumber: String(snap.problemNumber),
          initialStability: sched.stability,
          initialDifficulty: sched.difficulty,
          nextReviewAt: sched.nextReview.toISOString()
        });
        queueMicrotask(() => this.bumpDbMutation());
      }
    } catch (err) {
      console.error('[StudyQuantifier] persistAttempt failed:', err);
    }
  }

  discardProblem(): void {
    if (this.problem.state !== 'evaluating') return;
    if (this.macro.state !== 'focus_running' && this.macro.state !== 'break_running') return;
    this.clearCurrentAttempt();
    this.attemptContext = null;
    this.problem = resetProblemTimer(this.problem);
    // Discard NO incrementa el problemNumber — se queda como estaba.
    this.lastMessage = null;
  }

  // --- Macro timer actions ---

  startSession(
    context?: SessionContext,
    options?: { focusMs?: number; breakMs?: number; openEnded?: boolean },
    force = false
  ): void {
    if (force || this.macro.state === 'finished' || this.macro.state === 'discarded') {
      this.resetSession();
    }
    if (this.macro.state === 'inactive') {
      // GUARD: sin contexto no arrancamos. Antes arrancaba el timer mudo,
      // sin `macroSessionId`, y todos los attempts quedaban huérfanos —
      // ese era el bug de "problemas que no se registran en la tabla".
      if (!context || !context.nodeId) {
        this.lastMessage = 'Configura tu sesión antes de empezar.';
        return;
      }

      const prevFocus = this.macro.focusDurationMs;
      const prevBreak = this.macro.breakDurationMs;
      const focusMs = options?.focusMs ?? prevFocus;
      const breakMs = options?.breakMs ?? prevBreak;
      const openEnded = options?.openEnded ?? false;
      this.macro = startFocus(createMacroTimer(focusMs, breakMs, openEnded));
      this.problemNumber = 1;
      this.clearCurrentAttempt();
      this.lastMessage = null;

      // Contexto y macroSessionId SIEMPRE se asignan — ya validamos arriba.
      this.context = context;
      this.activeNodeId = context.nodeId;
      this.nodeTimes = { [context.nodeId!]: 0 };
      this.contextChangedAt = Date.now();
      
      const id = newId();
      this.macroSessionId = id;
      const initialSnapshot = this.toSnapshot(this.macro);
      this.trackPersist(this.persistMacroSession(context, id, initialSnapshot));

      this.attemptsCorrect = 0;
      this.attemptsIncorrect = 0;
      this.attemptsUnresolved = 0;
      this.attemptsTotalMs = 0;
      void this.recalcProblemNumber();
    }
  }

  private async persistMacroSession(
    context: SessionContext,
    id: UUID,
    timerSnapshot: MacroTimerSnapshot
  ): Promise<void> {
    await createMacroSession({
      id,
      nodeId: context.nodeId!,
      mode: this.mode,
      focusDurationMs: this.macro.focusDurationMs,
      breakDurationMs: this.macro.breakDurationMs,
      timerSnapshot
    });
  }

  pauseSession(): void {
    if (this.macro.state === 'focus_running') {
      this.previousMacroState = 'focus_paused';
      this.macro = pauseFocus(this.macro);
      if (this.problem.state === 'running') {
        this.problem = stopProblemTimer(this.problem);
      }
      this.persistSnapshot();
    }
  }

  resumeSession(): void {
    if (this.macro.state === 'focus_paused') {
      this.macro = resumeFocus(this.macro);
      this.persistSnapshot();
    }
  }

  /**
   * Motor de tiempo: se llama periódicamente para verificar si el
   * bloque actual (focus o break) ha terminado y transicionar el estado.
   */
  tick(): void {
    // Countdown del problema: si llega a 0, auto-detener y pasar a evaluating.
    if (this.problem.state === 'running' && this.problem.mode === 'countdown') {
      const result = tickCountdown(this.problem);
      if (result.expired) {
        this.problem = stopProblemTimer(this.problem, { clampToCountdown: true });
        if (this.prefs?.prefs.alarmEnabled) {
          this.playBell('focus');
        }
      }
    }

    if (this.macro.state === 'focus_running') {
      const result = tickFocus(this.macro);
      if (result.transitionedTo) {
        // Consolidar el tiempo del nodo activo antes de cerrar la sesión
        this.flushNodeTime();

        // Capturar datos del focus ANTES de mutar el estado
        const sid = this.macroSessionId;
        const elapsed = elapsedFocusMs(this.macro);
        const snap = this.toSnapshot(this.macro);
        const reading = this.macro.readingAccumulatedMs;
        const distractions = this.macro.distractions;

        // Limpiar el ID para que persistSnapshot no sobrescriba la sesión
        this.macroSessionId = null;

        // Finalizar la sesión de focus en la DB
        if (sid) {
          void (async () => {
            try {
              await finalizeMacroSession(sid, 'finished');
              await updateMacroSession(sid, {
                endedAt: new Date().toISOString(),
                focusDurationMs: elapsed,
                readingDurationMs: reading,
                distractions,
                status: 'finished',
                timerSnapshot: snap,
                nodeTimes: { ...this.nodeTimes }
              });
            } catch (e) {
              console.error('[SessionStore] Error finalizing focus session:', e);
            }
          })();
        }

        this.macro = result.timer;
        this.playBell('focus');
      }
    } else if (this.macro.state === 'break_running') {
      const result = tickBreak(this.macro);
      if (result.transitionedTo) {
        this.playBell('break');

        // Resetear a 'inactive' para que startSession() cree una NUEVA sesión
        this.macro = {
          ...this.macro,
          state: 'inactive',
          focusStartedAt: null,
          breakStartedAt: null
        };

        if (this.context) {
          void this.startSession(this.context, {
            focusMs: this.macro.focusDurationMs,
            breakMs: this.macro.breakDurationMs
          });
        }
      }
    }
  }

  soundEnabled = true;

  private playBell(type: 'focus' | 'break'): void {
    if (!this.soundEnabled) return;
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      // Frecuencia distinta: A5 para Focus, C5 para Break
      const freq = type === 'focus' ? 880 : 523.25;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
      osc.start();
      osc.stop(ctx.currentTime + 0.8);
    } catch (e) {
      console.warn('Audio play failed', e);
    }
  }


  finishSession(): void {
    if (this.problem.state === 'running') {
      this.problem = stopProblemTimer(this.problem);
    }
    this.macro = requestFinish(this.macro);
    this.persistSnapshot();
  }

  async finalizeSession(
    frustration: number | null = null,
    energy: number | null = null
  ): Promise<void> {
    // Consolidar el tiempo del nodo activo antes de cerrar la sesión manualmente
    this.flushNodeTime();
    await Promise.all(this.pendingPersists);
    this.macro = confirmFinish(this.macro);
    await this.persistMacroFinish(frustration, energy);
  }

  private async persistMacroFinish(
    frustration: number | null = null,
    energy: number | null = null
  ): Promise<void> {
    if (!this.macroSessionId) return;
    await finalizeMacroSession(this.macroSessionId, 'finished');
    await updateMacroSession(this.macroSessionId, {
      endedAt: new Date().toISOString(),
      focusDurationMs: elapsedFocusMs(this.macro),
      readingDurationMs: this.macro.readingAccumulatedMs,
      distractions: this.macro.distractions,
      frustration,
      energy
    });
    await updateMacroSession(this.macroSessionId, {
      status: 'finished',
      timerSnapshot: this.toSnapshot(this.macro),
      nodeTimes: { ...this.nodeTimes }
    });
    this.macroSessionId = null;
  }

  returnFromPending(): void {
    if (this.macro.state !== 'pending_finish') return;
    const t = Date.now();
    const target = this.previousMacroState;
    this.macro = {
      ...this.macro,
      state: target,
      focusStartedAt: target === 'focus_running' ? t : null,
      breakStartedAt: target === 'break_running' ? t : null,
      pausedAt: target === 'focus_paused' || target === 'break_paused' ? t : null
    };
    this.persistSnapshot();
  }

  private clearSessionMetrics(): void {
    this.attemptsCorrect = 0;
    this.attemptsIncorrect = 0;
    this.attemptsUnresolved = 0;
    this.attemptsTotalMs = 0;
  }

  /**
   * Borra (soft) todos los artefactos hijos de una sesión macro: attempts,
   * recall items derivados y conjeturas. Devuelve cuando termina.
   * Usado tanto por `discardSession` como por `resetActiveSession`.
   */
  private async cleanupSessionArtifacts(sessionId: UUID): Promise<void> {
    // Separamos cada nivel en su propio try/catch: si falla la carga de
    // conjeturas no queremos que se cancelen también los attempts. Antes
    // un solo fallo del `Promise.all` dejaba todo huérfano.
    let attempts: ProblemAttempt[] = [];
    let conjectures: Conjecture[] = [];

    try {
      attempts = await listAttemptsBySession(sessionId);
    } catch (err) {
      console.error('[StudyQuantifier] cleanup: listAttemptsBySession failed:', err);
    }

    try {
      conjectures = await listConjecturesBySession(sessionId);
    } catch (err) {
      console.error('[StudyQuantifier] cleanup: listConjecturesBySession failed:', err);
    }

    for (const c of conjectures) {
      try {
        await deleteConjecture(c.id);
      } catch (err) {
        console.error('[StudyQuantifier] cleanup: deleteConjecture failed:', err);
      }
    }

    for (const a of attempts) {
      try {
        const items = await listRecallItemsByAttempt(a.id);
        for (const it of items) {
          await softDeleteRecallItem(it.id);
        }
      } catch (err) {
        console.error('[StudyQuantifier] cleanup: recall items failed:', err);
      }
      try {
        await softDeleteProblemAttempt(a.id);
      } catch (err) {
        console.error('[StudyQuantifier] cleanup: softDeleteProblemAttempt failed:', err);
      }
    }
  }

  /**
   * Descarta la sesión activa: borra TODOS los artefactos hijos (attempts,
   * recall items, conjeturas) del storage y marca el macro como discarded.
   * El usuario que decide descartar espera que no quede rastro.
   */
  async discardSession(): Promise<void> {
    const id = this.macroSessionId;
    const snap = this.toSnapshot({ ...this.macro, state: 'discarded' });

    // 1. Limpiar los hijos ANTES de tocar el estado en memoria — si algo
    //    falla, el usuario puede reintentar (la sesión sigue activa).
    if (id) {
      await this.cleanupSessionArtifacts(id);
    }

    // 2. Ahora sí, reset del store y persistir el macro como discarded.
    this.macro = discardSession(this.macro);
    this.problem = resetProblemTimer(this.problem);
    this.problemNumber = 1;
    this.clearCurrentAttempt();
    this.clearSessionMetrics();
    this.context = null;
    this.attemptContext = null;
    this.macroSessionId = null;
    this.lastMessage = null;

    if (id) {
      this.trackPersist(
        updateMacroSession(id, { status: 'discarded', timerSnapshot: snap })
      );
    }

    this.bumpDbMutation();
  }

  resetSession(): void {
    const focusMs = this.macro.focusDurationMs;
    const breakMs = this.macro.breakDurationMs;
    const openEnded = this.macro.openEnded;
    this.macro = createMacroTimer(focusMs, breakMs, openEnded);
    this.problem = resetProblemTimer(this.problem);
    this.problemNumber = 1;
    this.clearCurrentAttempt();
    this.clearSessionMetrics();
    this.context = null;
    this.attemptContext = null;
    this.macroSessionId = null;
    this.lastMessage = null;
  }

  // --- Scope switching (in-session) ---

  /**
   * Actualiza la cadena de clasificación FSRS en caliente, sin cortar el
   * bloque Pomodoro. El próximo intento y su recall item heredan la cadena
   * nueva; los intentos ya guardados mantienen la suya.
   */
  updateSessionContext(patch: Partial<SessionContext>): void {
    if (!this.context) {
      if (!patch.nodeId) return;
      this.context = { nodeId: patch.nodeId, path: patch.path ?? [] };
      return;
    }

    const next: SessionContext = {
      nodeId: patch.nodeId !== undefined ? patch.nodeId : this.context.nodeId,
      path: patch.path !== undefined ? patch.path : this.context.path
    };

    if (next.nodeId === this.context.nodeId) return;

    this.context = next;
    // El cambio de nodo NO se persiste aquí. Se hará al llamar a commitContextChange.
    this.fsrsCache.clear();
  }

  /**
   * Consolida el tiempo transcurrido en el nodo activo dentro de `nodeTimes`
   * SIN cambiar de nodo. Se usa antes de cerrar la sesión (fin de focus o
   * finalización manual) para no perder el tiempo del último nodo.
   */
  flushNodeTime(): void {
    if (!this.activeNodeId) return;
    const now = Date.now();
    const elapsed = now - (this.contextChangedAt ?? now);
    if (elapsed > 0) {
      this.nodeTimes[this.activeNodeId] =
        (this.nodeTimes[this.activeNodeId] ?? 0) + elapsed;
    }
    this.contextChangedAt = now;
  }

  /**
   * Consolida el tiempo transcurrido en el nodo activo y actualiza la sesión
   * al nuevo nodo seleccionado. Se llama desde el botón "Guardar" o por
   * el temporizador de inactividad (60s).
   */
  commitContextChange(): void {
    if (!this.context?.nodeId || !this.activeNodeId) return;
    if (this.context.nodeId === this.activeNodeId) return;

    const now = Date.now();
    const elapsed = now - (this.contextChangedAt ?? now);

    // Acumular el tiempo en el nodo que estaba activo
    this.nodeTimes[this.activeNodeId] = (this.nodeTimes[this.activeNodeId] ?? 0) + elapsed;
    
    // Cambiar el nodo activo
    this.activeNodeId = this.context.nodeId;
    // Garantizar que el nuevo nodo tenga una entrada en nodeTimes
    this.nodeTimes[this.activeNodeId] = this.nodeTimes[this.activeNodeId] ?? 0;
    this.contextChangedAt = now;

    if (this.macroSessionId) {
      this.trackPersist(
        updateMacroSession(this.macroSessionId, {
          nodeId: this.activeNodeId,
          nodeTimes: { ...this.nodeTimes }
        })
      );
    }

    // Si el timer del problema está idle, recalcular Problem # para la
    // cadena nueva. Si está corriendo/evaluando, respetar el attemptContext
    // congelado — el problema en curso pertenece a la cadena anterior.
    if (this.problem.state === 'idle') {
      void this.recalcProblemNumber();
    }
  }

  // --- Auxiliary actions ---

  registerDistraction(): void {
    if (this.macro.state !== 'focus_running') return;
    this.macro = addDistraction(this.macro);
    this.persistSnapshot();
  }

  registerReading(ms: number): void {
    if (this.macro.state !== 'focus_running') return;
    this.macro = addReadingTime(this.macro, ms);
    this.persistSnapshot();
  }

  clearMessage(): void {
    this.lastMessage = null;
  }

  // --- Conjectures (in-session) ---

  /**
   * Guarda una conjetura indexada por la cadena FSRS activa.
   * Si hay un problema corriendo o pendiente de calificar, adjunta su
   * número; si no, la conjetura queda asociada solo a la cadena y a la
   * sesión macro.
   */
  async saveConjecture(content: string): Promise<void> {
    const trimmed = content.trim();
    if (!trimmed) return;
    if (!this.context) {
      this.lastMessage = 'No hay un contexto activo para guardar la conjetura.';
      return;
    }

    const problemActive =
      this.problem.state === 'running' || this.problem.state === 'evaluating';

    await createConjecture({
      nodeId: this.context.nodeId!,
      problemNumber: problemActive ? String(this.problemNumber) : null,
      sessionId: this.macroSessionId,
      content: trimmed
    });

    this.bumpDbMutation();
  }

  // --- Session reset with loss-aversion ---

  /**
   * Cuenta los artefactos generados durante la sesión activa, para que la
   * UI pueda cuantificar la pérdida antes de confirmar el reset.
   */
  async countActiveSessionArtifacts(): Promise<{
    problems: number;
    conjectures: number;
    focusMs: number;
  }> {
    if (!this.macroSessionId) {
      return { problems: 0, conjectures: 0, focusMs: 0 };
    }
    const [attempts, conjectures] = await Promise.all([
      listAttemptsBySession(this.macroSessionId),
      listConjecturesBySession(this.macroSessionId)
    ]);
    return {
      problems: attempts.length,
      conjectures: conjectures.length,
      focusMs: elapsedFocusMs(this.macro)
    };
  }

  /**
   * Borra todos los artefactos de la sesión activa (attempts + recall
   * items derivados + conjeturas) y luego resetea el estado en memoria.
   * No borra la fila del macro session: el caller decide si después
   * llama a discardSession() o simplemente a resetSession().
   */
  async resetActiveSession(): Promise<void> {
    const id = this.macroSessionId;
    if (id) {
      await this.cleanupSessionArtifacts(id);
    }
    this.resetSession();
    this.bumpDbMutation();
  }

  // --- Mode B: active recall ---

  async loadDueRecallItems(): Promise<number> {
    const due = await listDueRecallItems();
    return due.length;
  }

  async enterModeB(): Promise<boolean> {
    this.reviewLoading = true;
    try {
      const due = await listDueRecallItems();
      if (due.length === 0) {
        this.lastMessage = 'No hay problemas vencidos para repasar.';
        return false;
      }
      this.reviewGroups = await buildRecallGroups(due);
    } finally {
      this.reviewLoading = false;
    }
    this.selectedGroupKey = null;
    this.reviewBatch = [];
    this.reviewIndex = 0;
    this.reviewConsultedSolution = false;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.mode = 'B';
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
    return true;
  }

  selectGroup(key: string): void {
    if (this.mode !== 'B') return;
    const group = this.reviewGroups.find((g) => g.key === key);
    if (!group || group.items.length === 0) return;
    this.selectedGroupKey = key;
    this.reviewBatch = [...group.items];
    this.reviewIndex = 0;
    this.reviewConsultedSolution = false;
    this.reviewElapsedDays = this.computeElapsedDays(group.items[0]!);
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
  }

  async exitToGroups(): Promise<void> {
    if (this.mode !== 'B') return;
    this.selectedGroupKey = null;
    this.reviewBatch = [];
    this.reviewIndex = 0;
    this.reviewConsultedSolution = false;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
    await this.refreshReviewGroups();
    if (this.reviewGroups.length === 0) {
      this.exitModeB();
    }
  }

  async refreshReviewGroups(): Promise<void> {
    const due = await listDueRecallItems();
    this.reviewGroups = due.length === 0 ? [] : await buildRecallGroups(due);
  }

  get mostUrgentGroupKey(): string | null {
    if (this.reviewGroups.length === 0) return null;
    let best: { key: string; date: string } | null = null;
    for (const g of this.reviewGroups) {
      for (const item of g.items) {
        if (!best || item.nextReviewAt < best.date) {
          best = { key: g.key, date: item.nextReviewAt };
        }
      }
    }
    return best?.key ?? null;
  }

  exitModeB(): void {
    this.mode = 'A';
    this.reviewGroups = [];
    this.selectedGroupKey = null;
    this.reviewBatch = [];
    this.reviewIndex = 0;
    this.reviewConsultedSolution = false;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
  }

  rateCurrentRecallOutcome(outcome: 'good' | 'again'): void {
    if (this.mode !== 'B') return;
    if (this.problem.state !== 'evaluating') return;
    if (this.currentRecallRated) return;
    if (!this.currentRecall) return;
    this.currentRecallOutcome = outcome;
    this.lastMessage = null;
  }

  // Cancela el recall activo sin persistir nada: como si el usuario nunca
  // hubiera interactuado con el ítem actual. Se usa cuando el Pomodoro
  // termina mientras hay un problema en curso — el ítem no debe contarse.
  cancelActiveRecall(): void {
    if (this.mode !== 'B') return;
    if (this.problem.state === 'idle') return;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.reviewConsultedSolution = false;
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
  }

  async commitCurrentRecall(): Promise<void> {
    if (this.mode !== 'B') return;
    if (this.problem.state !== 'evaluating') return;
    if (this.currentRecallOutcome === null) {
      this.lastMessage = 'Califica el ítem para continuar.';
      return;
    }
    const outcome = this.currentRecallOutcome;
    await this.rateCurrentRecall(outcome);
    if (this.reviewIndex + 1 >= this.reviewBatch.length) {
      await this.exitToGroups();
      return;
    }
    this.reviewIndex += 1;
    this.reviewConsultedSolution = false;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    const nextItem = this.reviewBatch[this.reviewIndex];
    if (nextItem) this.reviewElapsedDays = this.computeElapsedDays(nextItem);
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
  }

  get currentRecall(): RecallItem | null {
    if (this.mode !== 'B') return null;
    return this.reviewBatch[this.reviewIndex] ?? null;
  }

  setConsultedSolution(value: boolean): void {
    this.reviewConsultedSolution = value;
  }

  async rateCurrentRecall(outcome: 'good' | 'again'): Promise<void> {
    if (this.mode !== 'B') return;
    if (this.problem.state !== 'evaluating') return;
    const item = this.currentRecall;
    if (!item) return;

    if (this.currentRecallRated) return;
    this.currentRecallRated = true;

    const rating: ReviewRating = outcome === 'good' ? (3 as ReviewRating) : (1 as ReviewRating);
    const state = {
      stability: item.stability,
      difficulty: item.difficulty,
      reps: item.reps,
      lapses: 0,
      lastReview: item.lastReviewAt ? new Date(item.lastReviewAt) : undefined,
      state: 'review' as const
    };

    const fsrs = await resolveFsrsForNode(item.nodeId, {
      retentionTarget: DEFAULT_FSRS_RETENTION,
      maximumIntervalDays: DEFAULT_MAX_INTERVAL_DAYS
    });
    const retentionTarget = fsrs.retentionTarget;
    const maximumInterval = fsrs.maximumIntervalDays;
    const sched = scheduleReview(state, rating, {
      params: { retentionTarget, maximumInterval }
    });
    const reviewedAt = new Date();
    const elapsedDays = this.computeElapsedDays(item);
    const durationMs = this.problem.elapsedMs;
    const consulted = this.reviewConsultedSolution;

    await this.persistReview(
      item,
      sched,
      rating,
      elapsedDays,
      durationMs,
      consulted,
      reviewedAt
    );
  }

  private computeElapsedDays(item: RecallItem): number {
    if (!item.lastReviewAt) return 0;
    const ms = Date.now() - new Date(item.lastReviewAt).getTime();
    return Math.max(0, ms / 86_400_000);
  }

  private fsrsCache = new Map<string, ResolvedFsrs>();

  invalidateRetentionCache(): void {
    this.fsrsCache.clear();
  }

  private async resolveFsrs(nodeId: string): Promise<ResolvedFsrs> {
    const key = nodeId;
    const cached = this.fsrsCache.get(key);
    if (cached) return cached;
    const resolved = await resolveFsrsForNode(nodeId, {
      retentionTarget: DEFAULT_FSRS_RETENTION,
      maximumIntervalDays: DEFAULT_MAX_INTERVAL_DAYS
    });
    this.fsrsCache.set(key, resolved);
    return resolved;
  }

  private async persistReview(
    item: RecallItem,
    sched: ReturnType<typeof scheduleReview>,
    rating: ReviewRating,
    elapsedDays: number,
    durationMs: number,
    consulted: boolean,
    reviewedAt: Date
  ): Promise<void> {
    try {
      await createReviewEvent({
        recallItemId: item.id,
        rating,
        scheduledDays: sched.intervalDays,
        elapsedDays,
        durationMs,
        consultedSolution: consulted,
        reviewedAt: reviewedAt.toISOString()
      });
      await updateRecallAfterReview(item.id, {
        stability: sched.stability,
        difficulty: sched.difficulty,
        reps: sched.reps,
        nextReviewAt: sched.nextReview.toISOString(),
        lastReviewAt: reviewedAt.toISOString()
      });
      this.bumpDbMutation();
    } catch (err) {
      console.error('[StudyQuantifier] persistReview failed:', err);
    }
  }

  nextRecall(): void {
    if (this.mode !== 'B') return;
    if (this.problem.state !== 'evaluating') return;
    if (this.reviewIndex + 1 >= this.reviewBatch.length) {
      this.exitModeB();
      return;
    }
    this.reviewIndex += 1;
    this.reviewConsultedSolution = false;
    const nextItem = this.reviewBatch[this.reviewIndex];
    if (nextItem) this.reviewElapsedDays = this.computeElapsedDays(nextItem);
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
  }

  // --- Forecast ---

  async loadForecast(days: number = 30): Promise<void> {
    const items = await listActiveRecallItems();
    this.forecastDays = computeForecast(items, days);
    this.forecastStats = computeStats(items);
  }

  async selectForecastDate(dateIso: string): Promise<void> {
    this.selectedForecastDate = dateIso;
    const items = await listActiveRecallItems();
    const todayKey = dayKeyFromIso(new Date().toISOString());
    this.forecastItemsForDate = items.filter((i) => {
      const k = dayKeyFromIso(i.nextReviewAt);
      if (dateIso === todayKey) return k <= todayKey;
      return k === dateIso;
    });
  }

  async enterModeBForDate(dateIso: string): Promise<boolean> {
    const items = await listActiveRecallItems();
    const todayKey = dayKeyFromIso(new Date().toISOString());
    const dateItems = items.filter((i) => {
      const k = dayKeyFromIso(i.nextReviewAt);
      if (dateIso === todayKey) return k <= todayKey;
      return k === dateIso;
    });
    if (dateItems.length === 0) {
      this.lastMessage = 'No hay ítems para esa fecha.';
      return false;
    }
    this.reviewGroups = await buildRecallGroups(dateItems);
    this.selectedGroupKey = null;
    this.reviewBatch = [];
    this.reviewIndex = 0;
    this.reviewConsultedSolution = false;
    this.currentRecallOutcome = null;
    this.currentRecallRated = false;
    this.mode = 'B';
    this.problem = resetProblemTimer(this.problem);
    this.clearCurrentAttempt();
    this.lastMessage = null;
    return true;
  }

  async enterModeBForDueToday(): Promise<boolean> {
    return this.enterModeB();
  }

  // --- Derived getters ---

  get isFocusRunning(): boolean {
    return this.macro.state === 'focus_running';
  }

  get isProblemRunning(): boolean {
    return this.problem.state === 'running';
  }

  get isEvaluating(): boolean {
    return this.problem.state === 'evaluating';
  }

  get attemptsCount(): number {
    return this.attemptsCorrect + this.attemptsIncorrect + this.attemptsUnresolved;
  }

  get attemptsAverageMs(): number {
    if (this.attemptsCount === 0) return 0;
    return this.attemptsTotalMs / this.attemptsCount;
  }

  get elapsedFocusMs(): number {
    return elapsedFocusMs(this.macro);
  }
}